/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useStore } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerActions, layerSelectors, layerUtils } from '@opengeoweb/store';

/**
 * Selects a single feature of a feature layer and puts it in edit mode, the same
 * way as clicking it on the map does (handy when shapes overlap). Readonly
 * features are only selected.
 * @param layerId id of the feature layer
 * @returns callback to select a feature by its feature id
 */
export const useSelectDrawFeature = (
  layerId?: string,
): ((featureId: string) => void) => {
  const dispatch = useDispatch();
  const store = useStore<CoreAppStore>();

  return React.useCallback(
    (featureId: string): void => {
      const layer = layerId
        ? layerSelectors.getLayerById(store.getState(), layerId)
        : undefined;
      const feature =
        layer?.geojson?.features[
          layerUtils.findFeatureIndexById(layer.geojson, featureId)
        ];
      // the map ignores clicks on shapes while one is being drawn, so do the same
      if (
        !layerId ||
        !feature ||
        layerUtils.isHiddenFeature(feature) ||
        layer?.drawMode
      ) {
        return;
      }

      dispatch(
        layerActions.setSelectedFeatures({
          layerId,
          selectedFeatureIds: [featureId],
        }),
      );
      dispatch(
        layerActions.toggleFeatureMode({
          layerId,
          isInEditMode: !feature.properties?.isReadonly,
          drawMode: '',
        }),
      );
    },
    [dispatch, layerId, store],
  );
};

/**
 * Deselects a feature of a feature layer and ends its edit mode, so the map no
 * longer highlights it.
 * @param layerId id of the feature layer
 * @returns callback to deselect a feature by its feature id
 */
export const useDeselectDrawFeature = (
  layerId?: string,
): ((featureId: string) => void) => {
  const dispatch = useDispatch();
  const store = useStore<CoreAppStore>();

  return React.useCallback(
    (featureId: string): void => {
      const selectedFeatureIds = layerSelectors.getSelectedFeatureIds(
        store.getState(),
        layerId,
      );
      if (!layerId || !selectedFeatureIds.includes(featureId)) {
        return;
      }

      dispatch(
        layerActions.setSelectedFeatures({
          layerId,
          selectedFeatureIds: selectedFeatureIds.filter(
            (selectedId) => selectedId !== featureId,
          ),
        }),
      );
      dispatch(
        layerActions.toggleFeatureMode({
          layerId,
          isInEditMode: false,
          drawMode: '',
        }),
      );
    },
    [dispatch, layerId, store],
  );
};
